import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { X, ArrowLeft } from "lucide-react";

export default function Datenschutz() {
  const navigate = useNavigate();
  const close = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  return (
    <div
      className="min-h-screen bg-[#FFFBFB] font-body text-qh-ink"
      data-testid="datenschutz-page"
    >
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-qh-line">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center shrink-0">
            <img
              src="/assets/qhearts-logo.png"
              alt="QHearts"
              className="h-14 sm:h-16 w-auto object-contain select-none"
              draggable="false"
            />
          </Link>
          <button
            onClick={close}
            className="hidden sm:inline-flex items-center gap-1.5 text-sm text-qh-slate hover:text-qh-ink rounded-full border border-qh-line px-4 py-2 transition-colors"
            data-testid="datenschutz-back"
          >
            <ArrowLeft className="w-4 h-4" /> Zurück
          </button>
          <button
            onClick={close}
            className="sm:hidden h-10 w-10 rounded-full border border-qh-line bg-white flex items-center justify-center"
            aria-label="Schließen"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      <motion.main
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-20"
      >
        <span className="text-xs uppercase tracking-[0.22em] font-semibold text-qh-rose">
          Rechtliches
        </span>
        <h1 className="mt-3 font-display font-medium text-4xl sm:text-5xl tracking-tight leading-tight">
          Datenschutz<span className="italic text-qh-rose">erklärung</span>
        </h1>
        <p className="mt-5 text-lg text-qh-slate leading-relaxed">
          Der Schutz deiner Daten ist für uns ein Herzensanliegen. Diese Seite
          fasst zusammen, was wir speichern, wofür und wie lange.
        </p>

        <div className="mt-14 space-y-10 text-[15px] sm:text-base leading-relaxed">
          <section>
            <h2 className="font-display text-2xl font-medium">
              1. Verantwortlicher
            </h2>
            <p className="mt-3 text-qh-ink/90">
              QHearts UG (haftungsbeschränkt), Deutschland.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-medium">
              2. Welche Daten wir verarbeiten
            </h2>
            <ul className="mt-3 space-y-2 text-qh-ink/90">
              <li className="flex gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-qh-rose shrink-0" />
                <span>
                  Profildaten (Name, E-Mail, Geburtsdatum, Fotos) — zur
                  Bereitstellung deines Kontos und der Plattform.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-qh-rose shrink-0" />
                <span>
                  Verifizierungsdaten (Ausweisbilder, Selfies, biometrische
                  Merkmale) — ausschließlich für Altersnachweis & Echtheit.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-qh-rose shrink-0" />
                <span>
                  Nutzungsdaten (Logins, Seitenaufrufe) — nur mit Einwilligung,
                  wenn du Analyse-Cookies aktivierst.
                </span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl font-medium">3. Cookies</h2>
            <p className="mt-3 text-qh-ink/90">
              Wir verwenden ausschließlich notwendige Cookies, sofern du keine
              weiteren Kategorien aktivierst. Deine Entscheidung kannst du
              jederzeit über den Footer-Link{" "}
              <strong className="font-medium">
                „Cookie-Einstellungen ändern"
              </strong>{" "}
              anpassen.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-medium">4. Deine Rechte</h2>
            <p className="mt-3 text-qh-ink/90">
              Du hast das Recht auf Auskunft, Berichtigung, Löschung,
              Einschränkung der Verarbeitung, Datenübertragbarkeit und
              Widerspruch. Eine Kontaktaufnahme ist jederzeit möglich.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-medium">5. Kontakt</h2>
            <p className="mt-3 text-qh-ink/90">
              Schreib uns an{" "}
              <a
                href="mailto:privacy@qhearts.me"
                className="text-qh-rose hover:text-qh-rosedark underline-offset-2 hover:underline"
              >
                privacy@qhearts.me
              </a>{" "}
              — wir antworten persönlich.
            </p>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t border-qh-line text-sm text-qh-slate">
          <p>
            <span className="font-semibold text-qh-ink">Stand:</span>{" "}
            {new Date().toLocaleDateString("de-DE", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
          <p className="mt-1">
            Diese Erklärung wird laufend aktualisiert. Vollständige Version
            folgt im offiziellen Launch.
          </p>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <button
            onClick={close}
            className="rounded-full bg-qh-ink hover:bg-qh-ink/90 text-white h-12 px-8 text-sm font-medium inline-flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Zurück zur Seite
          </button>
          <Link
            to="/agb"
            className="rounded-full border border-qh-line bg-white hover:bg-[#FFFBFB] h-12 px-8 text-sm font-medium inline-flex items-center justify-center transition-colors"
          >
            Zu den AGB
          </Link>
        </div>
      </motion.main>
    </div>
  );
}
